/**
 * Domain vocabulary that lives in core and shared, rendered in the chosen language.
 *
 * Roles, stock locations, account names, expense categories and event types are defined once —
 * in Spanish, because the server logs and the ledger are Spanish records — and translated only
 * on their way to an English screen. The Spanish map stays the fallback, so a term added to the
 * domain without a translation shows in Spanish rather than as a raw key.
 */

import {
  ROLE_LABELS,
  etiquetaEvento,
  etiquetaUbicacion,
  formatQuantity,
  type CuentaBancoConfig,
  type EventType,
  type Milli,
  type Role,
  type SettingKey,
  type UbicacionFija,
} from '@abastece/shared';
import {
  ACCOUNTS,
  ETIQUETA_CATEGORIA,
  type AccountKey,
  type CategoriaGasto,
} from '@abastece/core';
import { NOMBRE_AJUSTE } from '../lib/configuracion.js';
import { normalizar } from '../lib/texto.js';
import { idiomaActual } from './idioma.js';
import { traducir } from './index.js';

const en = () => idiomaActual() === 'en';

const ROL_EN: Record<Role, string> = {
  dueno: 'Owner',
  cajero: 'Cashier',
  repartidor: 'Driver',
};

const UBICACION_EN: Record<UbicacionFija, string> = {
  tienda: 'Shop floor',
  bodega: 'Storeroom',
  transformacion: 'Being repacked',
};

const CATEGORIA_EN: Record<CategoriaGasto, string> = {
  alquiler: 'Rent',
  energia: 'Electricity',
  agua: 'Water',
  telefono_internet: 'Phone and internet',
  transporte: 'Transport and fuel',
  mantenimiento: 'Maintenance and repairs',
  impuestos_municipales: 'Municipal taxes',
  empaques: 'Packaging and bags',
  sueldos: 'Wages',
  otros: 'Other expenses',
};

export function etiquetaRol(rol: Role): string {
  return en() ? (ROL_EN[rol] ?? ROLE_LABELS[rol]) : ROLE_LABELS[rol];
}

export function etiquetaLugar(ubicacion: string): string {
  if (!en()) return etiquetaUbicacion(ubicacion);
  if (ubicacion.startsWith('camion:')) return 'Truck';
  return UBICACION_EN[ubicacion as UbicacionFija] ?? etiquetaUbicacion(ubicacion);
}

export function etiquetaCategoriaGasto(c: CategoriaGasto): string {
  return en() ? (CATEGORIA_EN[c] ?? ETIQUETA_CATEGORIA[c]) : ETIQUETA_CATEGORIA[c];
}

/**
 * Product categories.
 *
 * A category is free text, so only the suggested ones — the words this system itself offers in
 * `CATEGORIAS_SUGERIDAS` and uses in the starter catalogue — have an English name. A category
 * somebody typed is the shop's own word and shows as typed, the same rule as a product's name.
 * Matched ignoring case and accents, because «granos basicos» typed by hand is the same shelf.
 */
const CATEGORIA_PRODUCTO_EN: Record<string, string> = {
  'granos basicos': 'Staples',
  abarrotes: 'Groceries',
  bebidas: 'Drinks',
  lacteos: 'Dairy',
  'carnes y embutidos': 'Meat and cold cuts',
  'frutas y verduras': 'Fruit and vegetables',
  panaderia: 'Bakery',
  golosinas: 'Sweets and snacks',
  limpieza: 'Cleaning',
  'higiene personal': 'Personal care',
  // Not "Medicine": that is the flag a medicine product carries, and a row reading
  // "Medicine · Medicine" says nothing twice.
  medicamentos: 'Pharmacy',
  ferreteria: 'Hardware',
  mayoreo: 'Wholesale',
  otros: 'Other',
};

export function etiquetaCategoriaProducto(categoria: string): string {
  if (!en()) return categoria;
  return CATEGORIA_PRODUCTO_EN[normalizar(categoria)] ?? categoria;
}

/**
 * Account names.
 *
 * Only the display name is translated. The code — 1100, 4100, 6240 — is the same on both
 * screens and in every export, which is what lets the two halves of a household talk about the
 * same line without agreeing on a language first.
 */
const CUENTA_EN: Record<AccountKey, string> = {
  CAJA_CORDOBAS: 'Till (drawer)',
  CAJA_DOLARES: 'Dollar till',
  CAJA_FUERTE: 'General cash',
  EFECTIVO_POR_RECIBIR: 'Cash to be received',
  BANCOS: 'Bank',
  BANCOS_2: 'Bank 2',
  BANCOS_3: 'Bank 3',
  BANCOS_4: 'Bank 4',
  BANCOS_5: 'Bank 5',
  BANCO_DOLARES: 'Dollar bank account',
  BANCO_DOLARES_2: 'Dollar bank account 2',
  BANCO_DOLARES_3: 'Dollar bank account 3',
  BANCO_DOLARES_4: 'Dollar bank account 4',
  BANCO_DOLARES_5: 'Dollar bank account 5',
  BILLETERA_MOVIL: 'Mobile wallet',
  INVENTARIO: 'Inventory',
  INVENTARIO_CAMION: 'Inventory on the truck',
  INVENTARIO_TRANSFORMACION: 'Inventory being repacked',
  CUENTAS_POR_COBRAR: 'Accounts receivable',
  ADELANTOS_EMPLEADOS: 'Salary advances',
  TRANSFERENCIAS_PENDIENTES: 'Unconfirmed transfers',
  SALDO_TELEFONICO: 'Phone credit',
  CUENTAS_POR_PAGAR: 'Accounts payable',
  AGUINALDO_POR_PAGAR: 'Aguinaldo payable',
  VACACIONES_POR_PAGAR: 'Holiday pay payable',
  INSS_POR_PAGAR: 'INSS payable',
  INATEC_POR_PAGAR: 'INATEC payable',
  IR_RETENIDO_POR_PAGAR: 'Income tax withheld',
  SUELDOS_POR_PAGAR: 'Wages payable',
  IVA_POR_PAGAR: 'VAT payable',
  CAPITAL: 'Capital',
  RETIROS_DUENO: "Owner's drawings",
  RESULTADOS_ACUMULADOS: 'Retained earnings',
  VENTAS: 'Sales',
  COMISIONES: 'Commissions',
  ENVIOS: 'Delivery charges',
  GANANCIA_CAMBIARIA: 'FX gain',
  SOBRANTE_CAJA: 'Till overage',
  SOBRANTE_INVENTARIO: 'Inventory overage',
  SOBRANTE_GENERAL: 'General cash overage',
  COSTO_VENTAS: 'Cost of sales',
  DESCUENTOS: 'Discounts given',
  DEVOLUCIONES_VENTAS: 'Customer returns',
  AJUSTE_COSTO_INVENTARIO: 'Inventory cost adjustment',
  SUELDOS: 'Wages and salaries',
  INSS_PATRONAL: 'INSS employer share',
  INATEC_GASTO: 'INATEC',
  AGUINALDO_GASTO: 'Aguinaldo',
  VACACIONES_GASTO: 'Holiday pay',
  INDEMNIZACION_GASTO: 'Severance',
  MERMA: 'Damage and spoilage',
  VENCIDOS: 'Expired stock',
  CONSUMO_INTERNO: 'Taken for the house',
  FALTANTE_INVENTARIO: 'Inventory shortfall',
  FALTANTE_REPARTO: 'Delivery shortfall',
  ALQUILER: 'Rent',
  ENERGIA: 'Electricity',
  AGUA: 'Water',
  TRANSPORTE: 'Transport and fuel',
  TELEFONO_INTERNET: 'Phone and internet',
  MANTENIMIENTO: 'Maintenance and repairs',
  IMPUESTOS_MUNICIPALES: 'Municipal taxes',
  EMPAQUES: 'Packaging and bags',
  OTROS_GASTOS: 'Other expenses',
  FALTANTE_CAJA: 'Till shortfall',
  REDONDEO: 'Cash rounding',
  TRANSFERENCIAS_PERDIDAS: 'Transfers never received',
  FALTANTE_GENERAL: 'General cash shortage',
  DIFERENCIAS_CONCILIACION: 'Reconciliation differences',
  FIADO_INCOBRABLE: 'Fiado written off',
  PERDIDA_CAMBIARIA: 'FX loss',
};

/** Code → key, built once, so a translation can be found from the code a report carries. */
const CLAVE_POR_CODIGO = new Map(
  (Object.entries(ACCOUNTS) as [AccountKey, { code: string }][]).map(([k, a]) => [a.code, k]),
);

/**
 * An account's name in the books, in the language on screen. A bank account the owner named
 * (`cuentasBanco`) is called that in either: «BAC dólares» is a name, not a word to translate.
 */
export function etiquetaCuenta(codigo: string, nombreEs: string, bancos?: readonly CuentaBancoConfig[]): string {
  const clave = CLAVE_POR_CODIGO.get(codigo);
  const puesta = clave && bancos?.find((c) => c.cuenta === clave);
  if (puesta) return puesta.nombre;
  if (!en()) return nombreEs;
  return clave ? CUENTA_EN[clave] : nombreEs;
}

/**
 * What an account means and what moves it, for the bubble on its line of the books (the owner,
 * 2026-10-17: «tooltips … so users can see what every line actually means and how it's
 * calculated»). One for every account of the chart, `cuentaAyuda.<key>` in both catalogues: an
 * account added without one does not compile.
 */
export function explicacionCuenta(codigo: string): string | undefined {
  const clave = CLAVE_POR_CODIGO.get(codigo);
  return clave ? traducir(`cuentaAyuda.${clave}`) : undefined;
}

/**
 * Event types, for the bitácora. Falls back to the Spanish label.
 *
 * With the payload, a PIN typed at the door says which door: a wrong PIN on the owner's
 * authorisation and a wrong PIN unlocking the screen are different things to be looking at. And
 * a change of setting says which setting, in the words «Configuración» uses for it: the day the
 * dollar rate is wrong, «Configuración cambiada» forty times over does not say which one moved.
 */
export function etiquetaEventoIdioma(tipo: string, payload?: unknown): string {
  const donde = (payload as { donde?: string } | null | undefined)?.donde;
  const porDonde =
    tipo === 'sesion.iniciada' || tipo === 'sesion.pin_incorrecto'
      ? SESION[tipo][donde as keyof (typeof SESION)[typeof tipo]]
      : undefined;
  if (porDonde) return en() ? porDonde.en : porDonde.es;
  const etiqueta = en() ? (EVENTO_EN[tipo as EventType] ?? etiquetaEvento(tipo)) : etiquetaEvento(tipo);
  const clave = tipo === 'config.cambiada' ? (payload as { clave?: string } | null | undefined)?.clave : undefined;
  const ajuste = clave ? NOMBRE_AJUSTE[clave as SettingKey] : undefined;
  return ajuste ? `${etiqueta}: ${traducir(ajuste)}` : etiqueta;
}

const SESION = {
  'sesion.iniciada': {
    entrada: { es: 'Inicio de sesión', en: 'Signed in' },
    desbloqueo: { es: 'Pantalla desbloqueada', en: 'Screen unlocked' },
  },
  'sesion.pin_incorrecto': {
    entrada: { es: 'PIN incorrecto al entrar', en: 'Wrong PIN signing in' },
    desbloqueo: { es: 'PIN incorrecto al desbloquear', en: 'Wrong PIN unlocking' },
    autorizacion: { es: 'PIN del dueño incorrecto', en: "Wrong owner's PIN" },
    confirmacion: { es: 'PIN incorrecto al confirmar', en: 'Wrong PIN confirming' },
  },
} as const;

const EVENTO_EN: Record<EventType, string> = {
  'usuario.creado': 'User created',
  'usuario.actualizado': 'User changed',
  'usuario.pin_cambiado': 'PIN changed',
  'usuario.desactivado': 'User deactivated',
  'usuario.reactivado': 'User reactivated',
  'sesion.iniciada': 'Signed in',
  'sesion.pin_incorrecto': 'Wrong PIN',
  'dispositivo.registrado': 'Device registered',
  'dispositivo.revocado': 'Device revoked',
  'dispositivo.reactivado': 'Device reinstated',
  'producto.creado': 'New product',
  'producto.actualizado': 'Product changed',
  'producto.desactivado': 'Product deactivated',
  'producto.reactivado': 'Product reactivated',
  'unidad_venta.creada': 'New pack size',
  'unidad_venta.actualizada': 'Pack size changed',
  'unidad_venta.eliminada': 'Pack size removed',
  'lista_precio.creada': 'New price list',
  'lista_precio.actualizada': 'Price list changed',
  'lista_precio.eliminada': 'Price list removed',
  'precio.fijado': 'Price set',
  'oferta.creada': 'Offer',
  'oferta.terminada': 'Offer ended',
  'proveedor.creado': 'New supplier',
  'proveedor.actualizado': 'Supplier changed',
  'proveedor.desactivado': 'Supplier deactivated',
  'compra.registrada': 'Goods received',
  'compra.anulada': 'Purchase voided',
  'compra.costo_corregido': 'Purchase cost corrected',
  'compra.cantidad_corregida': 'Purchase quantity corrected',
  'compra.forma_pago_corregida': 'Purchase payment method corrected',
  'compra.devuelta': 'Return to supplier',
  'compra.pagada': 'Paid a supplier',
  'compra.pago_anulado': 'Supplier payment voided',
  'inventario.saldo_inicial': 'Opening stock',
  'inventario.costo_corregido': 'Stock cost corrected',
  'compra.saldo_inicial': 'Owed to a supplier from before',
  'cliente.creado': 'New client',
  'cliente.actualizado': 'Client changed',
  'cliente.desactivado': 'Client deactivated',
  'cliente.saldo_inicial': "A client's balance from before",
  'abono.registrado': 'Client payment',
  'abono.anulado': 'Client payment voided',
  'incobrable.registrado': 'Fiado written off',
  'incobrable.anulado': 'Fiado write-off undone',
  'pedido.tomado': 'Order taken',
  'pedido.actualizado': 'Order changed',
  'pedido.cancelado': 'Order cancelled',
  'jornada.abierta': 'Left on a route',
  'jornada.liquidada': 'Route settled',
  'inventario.reempaque': 'Repacking',
  'inventario.conteo': 'Stock count',
  'inventario.salida': 'Stock written off',
  'inventario.traslado': 'Transfer',
  'caja.abierta': 'Till opened',
  'caja.movimiento': 'Till movement',
  'caja.entregada': 'Till handed over',
  'caja.cerrada': 'Till counted',
  'caja.apertura_corregida': 'Till opening corrected',
  'caja.corte_corregido': 'Till count corrected',
  'efectivo.recibido': 'Cash received',
  'efectivo.arqueo': 'General cash counted',
  'cuenta.conciliada': 'Account reconciled',
  'efectivo.depositado': 'Bank deposit',
  'efectivo.deposito_anulado': 'Bank deposit voided',
  'efectivo.sacado_del_banco': 'Withdrawn from the bank',
  'banco.transferido': 'Transfer between accounts',
  'banco.transferencia_anulada': 'Transfer between accounts voided',
  'semana.cuadrada': 'Week squared up',
  'venta.registrada': 'Sale',
  'venta.anulada': 'Sale voided',
  'pago.confirmado': 'Transfer confirmed',
  'pago.rechazado': 'Transfer rejected',
  'devolucion.registrada': 'Customer return',
  'recarga.saldo_comprado': 'Phone credit bought',
  'recarga.saldo_contado': 'Phone credit counted',
  'recarga.registrada': 'Top-up sold',
  'empleado.contratado': 'Employee hired',
  'empleado.actualizado': 'Employee changed',
  'empleado.liquidado': 'Final settlement',
  'empleado.adelanto': 'Salary advance',
  'empleado.vacaciones': 'Holiday days rested',
  'planilla.corrida': 'Payroll run',
  'planilla.pagada': 'Payroll paid',
  'planilla.corregida': 'Payroll run corrected',
  'cotizaciones.pagadas': 'Contributions paid',
  'aguinaldo.pagado': 'Aguinaldo paid',
  'aguinaldo.corregido': 'Aguinaldo payment corrected',
  'gasto.registrado': 'Expense',
  'faltante.explicado': 'Shortage explained',
  'gasto.anulado': 'Expense voided',
  'gasto.pagado': 'Paid an expense bought on credit',
  'gasto.pago_anulado': 'Expense payment voided',
  'dueno.retiro': "Owner's drawing",
  'dueno.aporte': "Owner's contribution",
  'dueno.movimiento_anulado': "Owner's money voided",
  'config.cambiada': 'Setting changed',
};

/**
 * Unit names.
 *
 * `arroba`, `quintal` and `vara` keep their names in English too: they are Nicaraguan trade
 * measures, not translations waiting to happen, and an English word for them would be an
 * invention the supplier's invoice does not use.
 */
const UNIDAD_EN: Record<string, string> = {
  unidad: 'unit',
  docena: 'dozen',
  par: 'pair',
  libra: 'pound',
  onza: 'ounce',
  kilo: 'kilo',
  litro: 'litre',
  galon: 'gallon',
  ml: 'millilitre',
  metro: 'metre',
  yarda: 'yard',
  // What a package starts called on «Paquetes» (`nombreDePaquete`), and the same word typed by hand.
  paquete: 'pack',
  bolsa: 'bag',
};

export function etiquetaUnidad(codigo: string, etiquetaEs: string): string {
  return en() ? (UNIDAD_EN[codigo] ?? etiquetaEs) : etiquetaEs;
}

/**
 * A presentation as a choice at the foot of a line says it (the owner, 2026-10-18): «Unidad»,
 * «Paquete (12)», «Saco (100 libra)». Its name with a capital, as a button's, and how much it holds
 * after it, but for the one of a single base unit, which is that unit. What it holds is a count
 * where the product is counted in units, and so much of the base unit otherwise.
 */
export function etiquetaPresentacion(u: { nombre: string; factor: number }, unidadBase: string): string {
  const nombre = etiquetaUnidad(u.nombre, u.nombre);
  const suyo = nombre.charAt(0).toLocaleUpperCase() + nombre.slice(1);
  if (u.factor === 1000) return suyo;
  const cuanto = formatQuantity(u.factor as Milli);
  return `${suyo} (${unidadBase === 'unidad' ? cuanto : `${cuanto} ${etiquetaUnidad(unidadBase, unidadBase)}`})`;
}

/** The same in every language: what a weight or a volume is written with after its number. */
const UNIDAD_CORTA: Record<string, string> = { libra: 'lb', litro: 'L', metro: 'm' };

/** «lb», «L»: a base unit as it is written after a number. */
export function unidadCorta(unidadBase: string): string {
  return UNIDAD_CORTA[unidadBase] ?? etiquetaUnidad(unidadBase, unidadBase);
}

/** «29», «183 lb», «69 L»: a count needs no unit, a weight or a volume does. */
export function cantidadCorta(cantidad: Milli, unidadBase: string): string {
  const n = formatQuantity(cantidad);
  if (unidadBase === 'unidad') return n;
  return `${n} ${unidadCorta(unidadBase)}`;
}
